/***
 * Created on 2023/8/29
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect, useRef, useState} from 'react';
import ModalDialog from "../../../../../common/modal_dialog/modalDialog";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@tanstack/react-query";
import {apiGet, apiPost} from "../../../../../../service/api";
import {getErrorMessage} from 'common/ServiceCommon';
import {getPrimaryEmail} from 'common/ServiceUser';
import ic_sys_info_svg from '../../../../../../static/img/utils/ic-sys-info.svg';
import {Tooltip} from "react-tooltip";
import {useCountdown} from "../../../../../common/countdown";


function AddPhoneStep2({show=false, closeWizard, next, prev, metadata}) {
    const { t } = useTranslation();
    const modalRef = useRef();
    const [errorMessage, setErrorMessage] = useState();
    const [trustDevice, setTrustDevice] = useState(false);
    const {seconds, setSeconds} = useCountdown(metadata?.seconds || 0);

    const countdown = useQuery({
        queryKey: ['get_countdown'],
        queryFn: async () => {
            const data = await apiGet('/ajax/countdown');
            const now = new Date().getTime();
            const value = Math.round((now - data?.countdown) / 1000) || 0;
            setSeconds(value > 60 ? 0 : 60 - value);
            return data;
        }
    });

    const updateTwoStepMutation = useMutation({
        mutationFn: async (data) => await apiPost('/ajax/two_step', data),
        onSuccess: data => {
            console.log(data);
            next();
        },
        onError: (error, variables, context) => {
            console.log(error);
            setErrorMessage(getErrorMessage(t, error.cause?.code));
        }
    });

    const sendTwoStepCodeMutation = useMutation({
        mutationFn: async (data) => await apiPost('/ajax/send_two_step_code', data),
        onSuccess: data => {
            console.log(data);
            countdown.refetch({force: true, queryKey: ['get_countdown']});
        },
        onError: (error, variables, context) => {
            console.log(error);
            setErrorMessage(getErrorMessage(t, error.cause?.code));
        }
    });

    const onVerify = () => {
        setErrorMessage(null);
        updateTwoStepMutation.mutate({
            code: metadata.values.code,
            country_code: metadata.values.country_code,
            phone_number: metadata.values.phone,
            verify_type: metadata.values.verify_type,
            trust_device: trustDevice
        });
    }

    const onSendCode = async () => {
        await sendTwoStepCodeMutation.mutateAsync({
            event_type: 'Send',
            send_type: 'email'
        })
    }

    useEffect(() => {
        if (metadata?.values?.code?.length === 6) {
            modalRef?.current?.disableSubmitButton(false);
        }
        else {
            modalRef?.current?.disableSubmitButton(true);
        }
    }, [metadata?.values?.code]);

    return (
        <ModalDialog title={t('@Add phone number@')} showModal={show} closeModal={closeWizard} submitText={t('@Verify@')} submitAction={onVerify} ref={modalRef}>
            <div className="dialog-content">
                <div className="dialog__form-group">
                    <label>{t('@Please check your SMS and enter the validation code in the box below@')}</label>
                    <input type="text" name="code" pattern="\d*" maxLength={6} minLength={6} value={metadata?.values?.code || ''} onChange={metadata.onChange}/>
                </div>

                {errorMessage && <p className="text-error">{errorMessage}</p>}

                <p className="text-remind !mb-24">{t('@The code has been sent to your mobile device via SMS. You may request another code in a short period of time.@')}</p>

                <div className="comp-checkbox">
                    <label className="q-checkbox">
                        <input type="checkbox" name="checkbox-checked" value={trustDevice} onChange={e=>setTrustDevice(prev=>!prev)}/>
                        <div>
                            {t('@Trust this device@')}
                            <img
                                data-tooltip-id="trust-device"
                                data-tooltip-content={t('@You can access your account from a trusted computer without needing a verification.@')}
                                className="display-inline-block vertical-align-text-bottom pl-4 pr-4" src={ic_sys_info_svg}/>
                            <Tooltip id="trust-device" />
                        </div>
                    </label>
                </div>

                <div className="dialog__row-divide mt-20 mb-20"></div>
                <div className="dialog__form-group">
                    <label>{t('@Send Test Email@')}</label>
                    <p className="!mb-16">{t('@Ensure your email is valid and accessible, as it will be used to send verification codes if you cannot access your phone.@')}</p>
                    <div className="comp-input">
                        <div className="input-field--hybrid">
                            <input type="text" value={getPrimaryEmail()} disabled={true}/>
                            <button className="q-btn q-btn-default" onClick={onSendCode} disabled={seconds > 0}>
                                {seconds <= 0 ? t('@Send code@') : `${t('@Resend me a code@')} (${seconds})`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </ModalDialog>
    );
}

export default AddPhoneStep2;